import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material/";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import CropIcon from "@mui/icons-material/Crop";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { logToSentry } from "../../../../../config/Sentry";

export default function ImageCarousel({ images = [], onRotate, onCrop }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [localRotations, setLocalRotations] = useState({});

    if (!images.length) return null;

    const currentImgItem = images[currentIndex]?.image;
    const rotation = localRotations[currentImgItem?.id] ?? currentImgItem?.rotation ?? 0;
    const hasMultiple = images.length > 1;

    const handleRotateRight = (event) => {
        try {
            event.stopPropagation();
            const newAngle = (rotation + 90) % 360;
            setLocalRotations((r) => ({ ...r, [currentImgItem.id]: newAngle }));
            onRotate?.(currentImgItem, { rotation: newAngle });
        } catch (error) {
            console.error(error);
            logToSentry("On rotating image in carousel:", { error, imageId: currentImgItem?.id });
        }
    };

    const handleCrop = (event) => {
        try {
            event.stopPropagation();
            onCrop?.(currentImgItem);
        } catch (error) {
            console.error(error);
            logToSentry("On cropping image in carousel:", { error, imageId: currentImgItem?.id });
        }
    };

    const prev = (event) => {
        try {
            event.stopPropagation();
            setCurrentIndex((i) => (i - 1 + images.length) % images.length);
        } catch (error) {
            console.error(error);
            logToSentry("On navigating to previous image in carousel:", { error });
        }
    };

    const next = (event) => {
        try {
            event.stopPropagation();
            setCurrentIndex((i) => (i + 1) % images.length);
        } catch (error) {
            console.error(error);
            logToSentry("On navigating to next image in carousel:", { error });
        }
    };

    return (
        <Box sx={{ display: "flex", width: "100%", height: "100%", minHeight: 0 }}>
            {/* Image area */}
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    position: "relative",
                    backgroundColor: "#111",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden"
                }}
            >
                <img
                    src={currentImgItem?.path}
                    alt=""
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        transform: `rotate(${rotation}deg)`,
                        transition: "transform 0.3s ease",
                        display: "block"
                    }}
                />

                {/* Prev / Next arrows */}
                {hasMultiple && (
                    <>
                        <IconButton
                            onClick={prev}
                            size="small"
                            sx={{
                                position: "absolute",
                                left: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                backgroundColor: "rgba(0,0,0,0.5)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            onClick={next}
                            size="small"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                backgroundColor: "rgba(0,0,0,0.5)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}
            </Box>

            {/* Right control strip */}
            <Box
                sx={{
                    width: 48,
                    flexShrink: 0,
                    backgroundColor: "#1a1a1a",
                    borderLeft: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    overflowY: "scroll",
                    py: 1.5,
                    gap: 1
                }}
            >
                {(currentImgItem?.imageType == null || currentImgItem?.imageType <= 2) && (
                    <Tooltip title="Crop" placement="left">
                        <IconButton
                            size="small"
                            onClick={handleCrop}
                            sx={{
                                color: "rgba(255,255,255,0.75)",
                                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" }
                            }}
                        >
                            <CropIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

                <Tooltip title="Rotate" placement="left">
                    <IconButton
                        size="small"
                        onClick={handleRotateRight}
                        sx={{
                            color: "rgba(255,255,255,0.75)",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" }
                        }}
                    >
                        <RotateRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Scrollable blank space for future items */}
                <Box sx={{ flex: 1, minHeight: 200 }} />
            </Box>
        </Box>
    );
}
